import { useMutation, useQuery } from 'react-query';
import { matchExternalRefsAxiosApi } from 'src/Config/axiosApi';

/**
 * Trade360 fixture-binding control plane — `/match-external-refs/*` on
 * `position-opener` (`matchExternalRefsAxiosApi`, base
 * `VITE_POSITION_OPENER_URL`, NOT the `/api/position-opener`-nested
 * `positionOpenerAxiosApi`). Lets an operator bind a group's match to a real
 * Trade360 fixture (`candidates` → `suggest` → `confirm`), flip whether the
 * External Live Signals framework actually acts on it (`acting`), and poll
 * what signals are arriving for a live-tested match (`events`).
 *
 * No list-by-group read exists on the wire — a confirmed binding is only
 * known for the lifetime of the dialog session that confirmed it. See
 * `Trade360FixtureSection`, the only consumer of these hooks.
 */

/** The only provider this framework binds to today. */
export const LSPORTS_TRADE360_PROVIDER = 'lsports-trade360';

export interface MatchExternalRefCandidate {
  fixtureId: string;
  participants: string[];
  league?: string;
}

export interface MatchExternalRefCompetitor {
  providerName: string;
  position: '1' | '2';
  code: string | null;
}

export interface SuggestMatchExternalRefResult {
  matchKey: string;
  provider: string;
  externalId: string;
  resolved: boolean;
  competitors: MatchExternalRefCompetitor[];
}

/** The persisted binding, as returned by `confirm`/`acting`. */
export interface MatchExternalRef {
  _id?: string;
  groupId?: string;
  matchKey: string;
  provider: string;
  externalId: string;
  competitorByPosition?: Record<'1' | '2', string>;
  actingEnabled?: boolean;
  boundBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Mirrors `LiveSignalEvent` on the backend (`live-signal-event.dto.ts`) —
 * field names must match exactly, this is a read-only ops view over the
 * corpus, not a shape either side is free to redefine independently. */
export interface MatchExternalRefEvent {
  signalType: string;
  subject: string | null;
  magnitude: number | null;
  /** Adapter receipt time, ms since epoch — NOT an ISO string. */
  observedAtMs: number;
}

/** `POST /match-external-refs/candidates` — search Trade360 fixtures for a match. */
export function useMatchExternalRefCandidates() {
  return useMutation(
    async (vars: { groupId: string; matchKey: string }) => {
      const { data } = await matchExternalRefsAxiosApi.post<
        { candidates: MatchExternalRefCandidate[] } | MatchExternalRefCandidate[]
      >('/match-external-refs/candidates', vars);
      return Array.isArray(data) ? data : (data?.candidates ?? []);
    },
    { mutationKey: 'match-external-refs:candidates' }
  );
}

/** `POST /match-external-refs/suggest` — preview roster resolution for a candidate. */
export function useSuggestMatchExternalRef() {
  return useMutation(
    async (vars: {
      groupId: string;
      matchKey: string;
      provider: string;
      externalId: string;
    }) => {
      const { data } =
        await matchExternalRefsAxiosApi.post<SuggestMatchExternalRefResult>(
          '/match-external-refs/suggest',
          vars
        );
      return data;
    },
    { mutationKey: 'match-external-refs:suggest' }
  );
}

/** `POST /match-external-refs/confirm` — persist the fixture binding. */
export function useConfirmMatchExternalRef() {
  return useMutation(
    async (vars: {
      groupId: string;
      matchKey: string;
      provider: string;
      externalId: string;
      competitorByPosition: Record<'1' | '2', string>;
      boundBy: string;
    }) => {
      const { data } = await matchExternalRefsAxiosApi.post<MatchExternalRef>(
        '/match-external-refs/confirm',
        vars
      );
      return data;
    },
    { mutationKey: 'match-external-refs:confirm' }
  );
}

/** `POST /match-external-refs/acting` — flip whether the framework acts on this fixture. */
export function useSetMatchExternalRefActing() {
  return useMutation(
    async (vars: {
      provider: string;
      externalId: string;
      actingEnabled: boolean;
      updatedBy: string;
    }) => {
      const { data } = await matchExternalRefsAxiosApi.post<MatchExternalRef>(
        '/match-external-refs/acting',
        vars
      );
      return data;
    },
    { mutationKey: 'match-external-refs:acting' }
  );
}

/** One row of `GET /match-external-refs/confirmed` — every binding made via
 * the group-dialog flow, across all groups, regardless of `actingEnabled`. */
export interface ConfirmedMatchExternalRef {
  matchKey: string;
  provider: string;
  externalId: string;
  competitorByPosition: Record<'1' | '2', string>;
  actingEnabled: boolean;
  boundAt: string;
}

export const MATCH_EXTERNAL_REFS_CONFIRMED_KEY = 'match-external-refs-confirmed';

/**
 * `GET /match-external-refs/confirmed` — every fixture ever bound, so the
 * Trade360 live-events tab can auto-surface a match the moment it is bound
 * from the group dialog, without the operator re-adding it there. Always
 * polling (unlike `events`, which only polls while a match is being looked
 * at) — this list drives what the tab even shows.
 */
export function useConfirmedMatchExternalRefs() {
  return useQuery<ConfirmedMatchExternalRef[]>(
    [MATCH_EXTERNAL_REFS_CONFIRMED_KEY],
    async () => {
      const { data } = await matchExternalRefsAxiosApi.get<
        ConfirmedMatchExternalRef[] | { refs: ConfirmedMatchExternalRef[] }
      >('/match-external-refs/confirmed');
      return Array.isArray(data) ? data : (data?.refs ?? []);
    },
    { refetchInterval: 12_000, keepPreviousData: true }
  );
}

/** One statistic row from a `peek` snapshot. Mirrors backend's
 * `PeekStatisticReading` (`peek-signal-extractor.ts`) — `statisticType` is
 * the numeric Trade360 statistic id (5 pressure, 8 penalty awarded, 40
 * penalty missed), not a string label. */
export interface MatchExternalRefPeekStatistic {
  statisticType: number;
  position: '1' | '2';
  value: number;
}

/** One scoreboard row from a `peek` snapshot. */
export interface MatchExternalRefPeekScoreboard {
  position: '1' | '2';
  value: number;
}

/** One participant from a `peek` snapshot. `position` is `null` when
 * Trade360 sent no explicit tag and there is no roster to fall back
 * against (mirrors backend's `PeekParticipant` exactly — never guessed). */
export interface MatchExternalRefPeekParticipant {
  position: '1' | '2' | null;
  name: string;
}

/** `GET /match-external-refs/peek` result — a point-in-time read straight off
 * Trade360's own API for an UNBOUND fixture (no matchKey/group binding yet).
 * Not a discrete event stream like `events` — just the current snapshot. */
export interface MatchExternalRefPeek {
  fixtureId: string;
  scoreboard: MatchExternalRefPeekScoreboard[] | null;
  statistics: MatchExternalRefPeekStatistic[] | null;
  participants: MatchExternalRefPeekParticipant[];
}

export const MATCH_EXTERNAL_REF_PEEK_KEY = 'match-external-ref-peek';

/**
 * `GET /match-external-refs/peek?fixtureId=&provider=` — polled ONLY while
 * the caller says so (`enabled`), same contract as `useMatchExternalRefEvents`:
 * this must not poll a fixture nobody is watching.
 */
export function useMatchExternalRefPeek(
  fixtureId: string | null,
  opts?: { enabled?: boolean }
) {
  return useQuery<MatchExternalRefPeek>(
    [MATCH_EXTERNAL_REF_PEEK_KEY, fixtureId],
    async () => {
      const { data } = await matchExternalRefsAxiosApi.get<MatchExternalRefPeek>(
        '/match-external-refs/peek',
        { params: { fixtureId, provider: LSPORTS_TRADE360_PROVIDER } }
      );
      return data;
    },
    {
      enabled: (opts?.enabled ?? true) && !!fixtureId,
      refetchInterval: 12_000,
      keepPreviousData: true
    }
  );
}

export const MATCH_EXTERNAL_REF_EVENTS_KEY = 'match-external-ref-events';

/**
 * `GET /match-external-refs/events?matchKey=&limit=` — polled ONLY while the
 * caller says so (`enabled`): the events panel starts collapsed, and this
 * must not poll a match nobody is looking at. Sorted reverse-chronological
 * here rather than trusting the wire order.
 */
export function useMatchExternalRefEvents(
  matchKey: string | null,
  opts?: { enabled?: boolean; limit?: number }
) {
  const limit = opts?.limit ?? 200;
  return useQuery<MatchExternalRefEvent[]>(
    [MATCH_EXTERNAL_REF_EVENTS_KEY, matchKey, limit],
    async () => {
      const { data } = await matchExternalRefsAxiosApi.get<
        MatchExternalRefEvent[] | { events: MatchExternalRefEvent[] }
      >('/match-external-refs/events', { params: { matchKey, limit } });
      const events = Array.isArray(data) ? data : (data?.events ?? []);
      return [...events].sort((a, b) => b.observedAtMs - a.observedAtMs);
    },
    {
      enabled: (opts?.enabled ?? true) && !!matchKey,
      refetchInterval: 5_000,
      keepPreviousData: true
    }
  );
}

/** One market a `MatchExternalRefAction` command applies to. `marketName` is
 * `null` when the backend hasn't resolved a human label yet — the raw
 * `ruleId` is the fallback, never a reason to hide the row. */
export interface MatchExternalRefActionMarket {
  ruleId: string;
  marketName: string | null;
}

/** One suspend/release/keepalive command the External Live Signals
 * framework actually issued for a bound match — the OUTPUT side, as opposed
 * to `MatchExternalRefEvent` (the raw Trade360 input signal that may or may
 * not have led to a command). `shadowMode` means the command was decided but
 * NOT applied against a real order — never render one as if it touched a
 * live order. */
export interface MatchExternalRefAction {
  matchKey: string;
  action: 'suspend' | 'release' | 'keepalive';
  commandId: string;
  shadowMode: boolean;
  reason: string;
  observedAtMs: number;
  markets: MatchExternalRefActionMarket[];
}

export const MATCH_EXTERNAL_REF_ACTIONS_KEY = 'match-external-ref-actions';

/**
 * `GET /match-external-refs/actions?matchKey=&limit=` — the suspend/
 * release/keepalive commands the framework actually issued for this match,
 * as opposed to `events`' raw input signals. Polled ONLY while the caller
 * says so (`enabled`), same contract as `useMatchExternalRefEvents`: must
 * not poll a match nobody is looking at. Sorted reverse-chronological here
 * rather than trusting the wire order.
 */
export function useMatchExternalRefActions(
  matchKey: string | null,
  opts?: { enabled?: boolean; limit?: number }
) {
  const limit = opts?.limit ?? 200;
  return useQuery<MatchExternalRefAction[]>(
    [MATCH_EXTERNAL_REF_ACTIONS_KEY, matchKey, limit],
    async () => {
      const { data } = await matchExternalRefsAxiosApi.get<
        MatchExternalRefAction[] | { actions: MatchExternalRefAction[] }
      >('/match-external-refs/actions', { params: { matchKey, limit } });
      const actions = Array.isArray(data) ? data : (data?.actions ?? []);
      return [...actions].sort((a, b) => b.observedAtMs - a.observedAtMs);
    },
    {
      enabled: (opts?.enabled ?? true) && !!matchKey,
      refetchInterval: 5_000,
      keepPreviousData: true
    }
  );
}
